/**
 * COMPTABILITÉ — DÉPOSER DES RAPPORTS EN LOT.
 *
 * On glisse une pile de PDF — les redditions Shotgun des soirées passées, un
 * rapport ZAP reçu ailleurs — et Gestion fait le reste : chaque fichier est
 * reconnu par son CONTENU (pas par son nom), rangé sous le bon fournisseur,
 * lu, et rattaché à sa nuit. L'écran dit, fichier par fichier, ce qu'il en a
 * fait : nuit, type, doublon, ou ce qui l'a arrêté.
 *
 * Déposer ne modifie aucune clôture : une source se confronte, elle ne
 * s'impose pas.
 *
 * ═══ DEUX HABILLAGES, UN SEUL DÉPÔT ═══
 * Sur l'écran des sources, la zone est un panneau de la page. Depuis
 * l'accueil, elle vit dans une fenêtre (« Ajouter des sources ») qui porte
 * déjà son titre : `nu` rend alors la zone et ses verdicts SANS le panneau,
 * plutôt qu'un second composant qui finirait par lire les fichiers autrement.
 * `onOccupe` prévient l'hôte qu'un envoi est en cours — une fenêtre qu'on
 * fermerait à ce moment perdrait le verdict de chaque fichier.
 */

import { useRef, useState, type ReactNode } from 'react'
import { CheckCircle2, CopyCheck, FileUp, Loader2, TriangleAlert, Upload } from 'lucide-react'
import { adminUpload } from '../admin/primitives.js'
import { nuitCourte } from './ClosingCommon.js'
import { VoirSource } from './SourceViewer.js'
import './finance-sources.css'

interface Resultat {
  cle: string
  nom: string
  etat: 'attente' | 'envoi' | 'ok' | 'refus'
  id?: string | undefined
  provider?: string | undefined
  report_type?: string | undefined
  parse_status?: string | undefined
  business_date?: string | null | undefined
  provider_report_id?: string | null | undefined
  duplicate?: boolean | undefined
  message?: string | undefined
}

const TYPES: Record<string, string> = {
  ZAP_REGISTER_SUMMARY: 'Rapport de caisse ZAP',
  ZAP_OTHER: 'Annexe ZAP',
  SHOTGUN_EVENT_REPORT: 'Reddition Shotgun',
  INCONNU: 'Document non reconnu',
}

/** Ce que `POST /finance/sources/drop` rend, fichier par fichier. */
interface ReponseDepot {
  error?: string; message?: string; id?: string; provider?: string; report_type?: string
  parse_status?: string; business_date?: string | null; provider_report_id?: string | null; duplicate?: boolean
}

/** Au-delà, ce n'est plus un dépôt : c'est une migration, à faire autrement. */
const MAX_FICHIERS = 60

export function DepotRapports(props: {
  onFini: () => void
  /** Sans le panneau : l'hôte (une fenêtre) porte déjà le titre. */
  nu?: boolean
  /** Vrai pendant l'envoi, faux à la fin — pour qu'une fenêtre ne se ferme pas en route. */
  onOccupe?: (occupe: boolean) => void
}) {
  const [survol, setSurvol] = useState(false)
  const [resultats, setResultats] = useState<Resultat[]>([])
  const [occupe, setOccupe] = useState(false)
  const champ = useRef<HTMLInputElement>(null)

  const deposer = async (liste: File[]) => {
    const fichiers = liste.slice(0, MAX_FICHIERS)
    if (fichiers.length === 0) return
    const depart = Date.now()
    const lignes: Resultat[] = fichiers.map((f, i) => ({ cle: `${depart}-${i}`, nom: f.name, etat: 'attente' }))
    setResultats(lignes)
    setOccupe(true)
    props.onOccupe?.(true)
    /* Le `finally` libère TOUJOURS la zone et la fenêtre hôte. Une coupure
       réseau fait REJETER `fetch` (une réponse d'erreur, elle, se lit) : sans
       lui, l'exception sortait de la boucle, « occupé » restait vrai, et la
       fenêtre « Ajouter des sources » ne se fermait plus qu'en rechargeant
       la page (revue du 07/10/2026). */
    try {
      // UN fichier à la fois : l'écran montre l'avancée, et un lot de soixante
      // PDF ne sature ni la lecture du serveur ni la limite de débit.
      for (let i = 0; i < fichiers.length; i++) {
        const f = fichiers[i]!
        setResultats((rs) => rs.map((r, j) => (j === i ? { ...r, etat: 'envoi' } : r)))
        const octets = new File([f], f.name, { type: 'application/octet-stream' })
        let r: { status: number; data: ReponseDepot }
        try {
          r = await adminUpload<ReponseDepot>('POST', `/api/v1/finance/sources/drop?${new URLSearchParams({ filename: f.name }).toString()}`, octets)
        } catch {
          // Le serveur ne répond plus : ce fichier et les suivants ne sont pas
          // partis. On s'arrête — chaque ligne dit ce qu'il en est.
          setResultats((rs) => rs.map((x, j) => {
            if (j === i) return { ...x, etat: 'refus', message: 'Le serveur n’a pas pu être joint : fichier non envoyé.' }
            if (j > i) return { ...x, etat: 'refus', message: 'Non envoyé : le dépôt s’est arrêté à la coupure, redéposez-le.' }
            return x
          }))
          break
        }
        setResultats((rs) => rs.map((x, j) => {
          if (j !== i) return x
          if (r.status >= 200 && r.status < 300 && r.data.id) {
            return {
              ...x, etat: 'ok', id: r.data.id, provider: r.data.provider, report_type: r.data.report_type,
              parse_status: r.data.parse_status, business_date: r.data.business_date ?? null,
              provider_report_id: r.data.provider_report_id ?? null, duplicate: r.data.duplicate === true,
            }
          }
          return { ...x, etat: 'refus', message: r.data.message ?? 'Fichier refusé par le serveur.' }
        }))
      }
    } finally {
      setOccupe(false)
      props.onOccupe?.(false)
      props.onFini()
    }
  }

  const lus = resultats.filter((r) => r.etat === 'ok')
  const bilan = resultats.length > 0 && !occupe ? {
    nouveaux: lus.filter((r) => !r.duplicate).length,
    doublons: lus.filter((r) => r.duplicate).length,
    refus: resultats.filter((r) => r.etat === 'refus').length,
    sansNuit: lus.filter((r) => !r.business_date).length,
  } : null

  const contenu: ReactNode = (
    <>
      <div
        className={`fsd-zone${survol ? ' fsd-zone--survol' : ''}${occupe ? ' fsd-zone--occupe' : ''}`}
        onDragOver={(e) => { e.preventDefault(); if (!occupe) setSurvol(true) }}
        onDragLeave={() => setSurvol(false)}
        onDrop={(e) => {
          e.preventDefault(); setSurvol(false)
          if (!occupe) void deposer([...e.dataTransfer.files])
        }}
      >
        <Upload size={24} aria-hidden="true" />
        <p className="fsd-zone__titre">Glissez ici vos PDF — redditions Shotgun, rapports ZAP</p>
        <p className="lc-hint">
          Gestion reconnaît chaque fichier par son contenu, le lit et le range dans sa nuit. Un fichier déjà
          reçu n’est pas dupliqué. Aucune clôture n’est modifiée.
        </p>
        <button type="button" className="lc-btn" disabled={occupe} onClick={() => champ.current?.click()}>
          Choisir des fichiers
        </button>
        <input
          ref={champ} type="file" multiple accept=".pdf,application/pdf" hidden
          onChange={(e) => { const l = [...(e.target.files ?? [])]; e.target.value = ''; void deposer(l) }}
        />
      </div>

      {resultats.length > 0 ? (
        <>
          {bilan ? (
            <p className="fsd-bilan" role="status">
              {bilan.nouveaux} nouveau{bilan.nouveaux > 1 ? 'x' : ''} · {bilan.doublons} déjà reçu{bilan.doublons > 1 ? 's' : ''}
              {bilan.refus > 0 ? ` · ${bilan.refus} refusé${bilan.refus > 1 ? 's' : ''}` : ''}
              {bilan.sansNuit > 0 ? ` · ${bilan.sansNuit} sans nuit (à rattacher depuis sa fiche)` : ''}
            </p>
          ) : null}
          <ul className="fsd-liste">
            {resultats.map((r) => (
              <li key={r.cle} className={`fsd-ligne fsd-ligne--${r.etat}`}>
                <span className="fsd-ligne__icone" aria-hidden="true">
                  {r.etat === 'envoi' || r.etat === 'attente' ? <Loader2 size={15} className={r.etat === 'envoi' ? 'lc-spin' : undefined} />
                    : r.etat === 'refus' ? <TriangleAlert size={15} />
                      : r.duplicate ? <CopyCheck size={15} /> : <CheckCircle2 size={15} />}
                </span>
                <span className="fsd-ligne__nom">
                  {r.id ? (
                    <VoirSource doc={{ id: r.id, filename: r.nom, provider: r.provider ?? null, report_type: r.report_type ?? null, business_date: r.business_date ?? null }} />
                  ) : r.nom}
                </span>
                <span className="fsd-ligne__verdict">
                  {r.etat === 'attente' ? 'En attente' : r.etat === 'envoi' ? 'Lecture en cours'
                    : r.etat === 'refus' ? r.message
                      : [
                        TYPES[r.report_type ?? 'INCONNU'] ?? r.report_type,
                        r.provider_report_id,
                        r.business_date ? `nuit du ${nuitCourte(r.business_date)}` : 'sans nuit',
                        r.duplicate ? 'déjà reçu' : null,
                        r.parse_status === 'NEEDS_REVIEW' && r.report_type !== 'ZAP_OTHER' ? 'à vérifier' : null,
                      ].filter(Boolean).join(' · ')}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </>
  )

  if (props.nu) return <div className="fsd-nu">{contenu}</div>
  return (
    <section className="lc-panel fsd-panel">
      <div className="lc-panel__head">
        <span className="fih-head__icon" aria-hidden="true"><FileUp size={17} /></span>
        <h2 className="lc-panel__title">Déposer des rapports</h2>
      </div>
      <div className="lc-panel__body">{contenu}</div>
    </section>
  )
}
